import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { supportedLanguages } from '../supportedLanguages';

/**
 * Le repli web de `/read/...` (#431).
 *
 * `https://bible4.me/read/fr/mt/5/1-12` est l'adresse que l'application
 * revendique. Sur un téléphone où elle est installée, le navigateur ne voit
 * jamais ce lien : Android et iOS l'ouvrent dans le lecteur. Partout ailleurs —
 * bureau, téléphone sans l'application — c'est ce composant qui répond, et il
 * renvoie sur la page de passage du site.
 *
 * Un renvoi plutôt qu'un rendu : `BibleContentPage` vit sous `LanguageLayout`,
 * dont il tient sa langue et sa traduction. Le monter ici, hors de ce contexte,
 * demanderait de rejouer ce que la route de langue fait déjà.
 *
 * `replace` évite de laisser `/read/...` dans l'historique : un retour arrière
 * y repasserait, et sur un téléphone équipé de l'application ce retour
 * rouvrirait l'application.
 */
const ReadPassageRedirect: React.FC = () => {
  const { lang, book, chapter, verses } = useParams<{
    lang: string;
    book: string;
    chapter: string;
    verses?: string;
  }>();
  const { search, hash } = useLocation();

  // Une langue inconnue vaut mieux corrigée que suivie : `/read/xx/mt/5` doit
  // mener au passage, pas à une page vide.
  const safeLang = lang && supportedLanguages.includes(lang) ? lang : 'fr';

  if (!book || !chapter) {
    return <Navigate to={`/${safeLang}`} replace />;
  }

  const versesPath = verses ? `/${verses}` : '';
  return (
    <Navigate
      to={`/${safeLang}/${book}/${chapter}${versesPath}${search}${hash}`}
      replace
    />
  );
};

export default ReadPassageRedirect;
